import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../config/firebase';
import {
  collection, query, where, orderBy, limit, onSnapshot,
  getCountFromServer, doc, updateDoc, setDoc, getDocs, deleteDoc, writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Icon from '../Shared/Icons';
import { C, FONT } from '../../utils/chessTheme';

// ── Chess Admin — kila kinachotokea kwenye ChessApp, mahali pamoja ─────
// For Iyanna's team only: live counts, the activity stream, and the
// verification queue — so decisions about what to fix or improve are
// made from real usage, not guesswork.

function fmtTime(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function ChessAdmin() {
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'staff';

  const [counts, setCounts] = useState({ players: null, games: null, tournaments: null, verified: null });
  const [liveGames, setLiveGames] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [pendingVerifs, setPendingVerifs] = useState([]);
  const [note, setNote] = useState('');
  const [players, setPlayers] = useState([]);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // player object
  const [penaltyFor, setPenaltyFor] = useState(null);       // player being penalised
  const [penaltyAmt, setPenaltyAmt] = useState('');
  const [working, setWorking] = useState(false);

  // Headline counts (server-side aggregation — cheap and accurate)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [p, g, t, v] = await Promise.all([
          getCountFromServer(collection(db, 'chessPlayers')),
          getCountFromServer(collection(db, 'chessGames')),
          getCountFromServer(collection(db, 'tournaments')),
          getCountFromServer(query(collection(db, 'chessPlayers'), where('verified', '==', true))),
        ]);
        setCounts({
          players: p.data().count,
          games: g.data().count,
          tournaments: t.data().count,
          verified: v.data().count,
        });
      } catch (e) { console.error(e); }
    })();
  }, [isAdmin]);

  // Live now
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'chessGames'), where('status', '==', 'active'));
    return onSnapshot(q, snap => setLiveGames(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [isAdmin]);

  // Activity stream — latest finished games
  useEffect(() => {
    if (!isAdmin) return;
    // Only finished games have finishedAt, so ordering by it alone
    // returns exactly the finished games — no composite index needed.
    const q = query(collection(db, 'chessGames'), orderBy('finishedAt', 'desc'), limit(15));
    return onSnapshot(q, snap => setRecentGames(snap.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error(e));
  }, [isAdmin]);

  // Verification queue
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'chessVerifications'), where('status', '==', 'pending'));
    return onSnapshot(q, snap => setPendingVerifs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [isAdmin]);

  // All players (for management) — ordered by rating
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'chessPlayers'), orderBy('rating', 'desc'));
    return onSnapshot(q, snap => setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [isAdmin]);

  function flash(m) { setNote(m); setTimeout(() => setNote(''), 2800); }

  // Reset EVERYONE to 0 — fresh, fair start before launch. Clears rating
  // and the win/loss/game tallies so the whole ladder begins together.
  async function resetAllRatings() {
    setWorking(true);
    try {
      const snap = await getDocs(collection(db, 'chessPlayers'));
      // Firestore batches cap at 500 ops — chunk to be safe.
      const docs = snap.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        docs.slice(i, i + 400).forEach(d => {
          batch.update(d.ref, {
            rating: 0, wins: 0, losses: 0, draws: 0,
            gamesPlayed: 0, meetGames: 0, todayDelta: 0, weekDelta: 0,
          });
        });
        await batch.commit();
      }
      flash(`Rating za wachezaji ${docs.length} zimerudishwa 0.`);
      setDangerOpen(false); setResetPhrase('');
    } catch (e) { console.error(e); flash('Imeshindikana — jaribu tena.'); }
    setWorking(false);
  }

  // Delete a single account (e.g. a test or duplicate). Removes the
  // leaderboard entry and their verification record.
  // Penalty — deduct SOME points from a player (discipline), never below 0.
  // Used for behaviour that harms the community, without wiping everything.
  async function deductPoints(p, amount) {
    const n = Math.abs(parseInt(amount, 10) || 0);
    if (!n) return;
    setWorking(true);
    try {
      const current = p.rating || 0;
      await updateDoc(doc(db, 'chessPlayers', p.id), { rating: Math.max(0, current - n) });
      flash(`${p.name} amekatwa pointi ${n}.`);
      setPenaltyFor(null);
    } catch (e) { console.error(e); flash('Imeshindikana kukata pointi.'); }
    setWorking(false);
  }

  async function deletePlayer(p) {
    setWorking(true);
    try {
      await deleteDoc(doc(db, 'chessPlayers', p.id));
      try { await deleteDoc(doc(db, 'chessVerifications', p.id)); } catch {}
      flash(`Akaunti ya ${p.name} imefutwa.`);
      setConfirmDelete(null);
    } catch (e) { console.error(e); flash('Imeshindikana kufuta.'); }
    setWorking(false);
  }

  async function decideVerification(v, approve) {
    try {
      await updateDoc(doc(db, 'chessVerifications', v.id), {
        status: approve ? 'approved' : 'rejected',
        decidedAt: new Date().toISOString(),
      });
      if (approve) {
        await setDoc(doc(db, 'chessPlayers', v.playerId), { verified: true }, { merge: true });
      }
      setNote(approve ? `${v.playerName} amethibitishwa ✓` : `Ombi la ${v.playerName} limekataliwa`);
      setTimeout(() => setNote(''), 2500);
    } catch (e) { console.error(e); }
  }

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <div style={styles.centerMsg}>
          Ukurasa huu ni wa timu ya Iyanna pekee. <Link to="/chess" style={styles.link}>Rudi Chess</Link>
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Wachezaji', value: counts.players, icon: 'user' },
    { label: 'Wamethibitishwa', value: counts.verified, icon: 'verified' },
    { label: 'Michezo Yote', value: counts.games, icon: 'chess' },
    { label: 'Live Sasa', value: liveGames.length, icon: 'play' },
    { label: 'Tournaments', value: counts.tournaments, icon: 'trophy' },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Chess Admin</div>
          <Link to="/chess" style={styles.backLink}>← Rudi Chess Lobby</Link>
        </div>
        <Link to="/chess/tournaments" style={styles.tourneyLink}>
          <Icon name="trophy" size={14} color="#06281A" strokeWidth={2.2} /> Tournaments
        </Link>
      </div>

      {/* HEADLINE STATS */}
      <div style={styles.statGrid}>
        {statCards.map(s => (
          <div key={s.label} style={styles.statCard}>
            <div style={styles.statIcon}><Icon name={s.icon} size={17} color={C.accent} /></div>
            <div style={styles.statNum}>{s.value ?? '—'}</div>
            <div style={styles.statLbl}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* VERIFICATION QUEUE */}
      <div style={styles.sectionLabel}>
        Maombi ya Uthibitisho {pendingVerifs.length > 0 && <span style={styles.badgeCount}>{pendingVerifs.length}</span>}
      </div>
      {pendingVerifs.length === 0 ? (
        <div style={styles.emptyRow}>Hakuna ombi linalosubiri.</div>
      ) : (
        <div style={styles.list}>
          {pendingVerifs.map(v => (
            <div key={v.id} style={styles.verifRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.verifName}>{v.fullName} <span style={styles.verifNick}>({v.playerName})</span></div>
                <div style={styles.verifMeta}>
                  {[v.age && `Umri ${v.age}`, v.school, v.guardianPhone].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button style={styles.approveBtn} onClick={() => decideVerification(v, true)}>
                <Icon name="check" size={14} color="#06281A" strokeWidth={2.5} />
              </button>
              <button style={styles.rejectBtn} onClick={() => decideVerification(v, false)}>
                <Icon name="x" size={14} color="#F87171" strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* LIVE NOW */}
      <div style={styles.sectionLabel}>Inaendelea Sasa</div>
      {liveGames.length === 0 ? (
        <div style={styles.emptyRow}>Hakuna mchezo unaoendelea.</div>
      ) : (
        <div style={styles.list}>
          {liveGames.map(g => (
            <Link key={g.id} to={`/chess/room/${g.id}`} style={styles.gameRow}>
              <span style={styles.liveDot} />
              <span style={styles.gameNames}>
                {g.white?.name || '—'} vs {g.black?.name || '—'}
                {g.isBotGame && <span style={styles.botTag}>BOT</span>}
                {g.tournamentId && <span style={styles.tourneyTag}>TOURNAMENT</span>}
              </span>
              <span style={styles.gameMeta}>{g.moveHistory?.length || 0} moves</span>
            </Link>
          ))}
        </div>
      )}

      {/* ACTIVITY STREAM */}
      <div style={styles.sectionLabel}>Shughuli za Karibuni</div>
      {recentGames.length === 0 ? (
        <div style={styles.emptyRow}>Bado hakuna michezo iliyokamilika.</div>
      ) : (
        <div style={styles.list}>
          {recentGames.map(g => (
            <Link key={g.id} to={`/chess/review/${g.id}`} style={styles.gameRow}>
              <span style={styles.gameNames}>
                {g.white?.name || '—'} vs {g.black?.name || '—'}
                {g.isBotGame && <span style={styles.botTag}>BOT</span>}
              </span>
              <span style={styles.gameResult}>
                {g.winner === 'draw' ? 'Sare' : g.winner === 'white' ? '1–0' : '0–1'}
              </span>
              <span style={styles.gameMeta}>{fmtTime(g.finishedAt)}</span>
            </Link>
          ))}
        </div>
      )}

      {/* PLAYER MANAGEMENT — fix legacy ratings & remove test accounts */}
      <div style={styles.sectionLabel}>Wachezaji ({players.length})</div>

      {/* Danger zone is hidden by default. Reset requires typing a phrase,
          so an accidental tap can never wipe everyone's ratings. */}
      {!dangerOpen ? (
        <button style={styles.dangerToggle} onClick={() => setDangerOpen(true)}>
          <Icon name="wrench" size={13} color={C.textFaint} /> Zana za hatari
        </button>
      ) : (
        <div style={styles.resetBox}>
          <div style={styles.resetInfo}>
            <div style={styles.resetTitle}>⚠ Rudisha Rating zote → 0</div>
            <div style={styles.resetText}>
              Hii inagusa KILA mchezaji na haiwezi kutenduliwa. Itumie mara moja tu
              kabla ya launch ili wote waanze sawa. Ili kuendelea, andika neno
              <strong> SIFURI </strong> hapa chini.
            </div>
          </div>
          <input
            style={styles.resetInput}
            value={resetPhrase}
            onChange={e => setResetPhrase(e.target.value)}
            placeholder="Andika: SIFURI"
          />
          <div style={styles.confirmRow}>
            <button
              style={{ ...styles.dangerBtn, ...(resetPhrase.trim().toUpperCase() !== 'SIFURI' ? styles.dangerBtnDisabled : {}) }}
              disabled={working || resetPhrase.trim().toUpperCase() !== 'SIFURI'}
              onClick={resetAllRatings}
            >
              {working ? 'Inarudisha…' : 'Rudisha zote → 0'}
            </button>
            <button style={styles.cancelBtn} onClick={() => { setDangerOpen(false); setResetPhrase(''); }}>Funga</button>
          </div>
        </div>
      )}

      <div style={styles.list}>
        {players.map(p => (
          <div key={p.id}>
            <div style={styles.playerRow}>
              <span style={styles.playerAvatar}>{(p.name || '?')[0].toUpperCase()}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.playerName}>{p.name}{p.verified && <Icon name="verified" size={12} color={C.accent} style={{ marginLeft: 5 }} />}</div>
                <div style={styles.playerMeta}>rating {p.rating ?? 0} · michezo {p.gamesPlayed || 0}</div>
              </div>
              {confirmDelete?.id === p.id ? (
                <div style={styles.confirmRow}>
                  <button style={styles.dangerBtnSm} disabled={working} onClick={() => deletePlayer(p)}>Futa</button>
                  <button style={styles.cancelBtnSm} onClick={() => setConfirmDelete(null)}>La</button>
                </div>
              ) : (
                <>
                  <button
                    style={styles.penaltyBtn}
                    onClick={() => { setPenaltyFor(penaltyFor === p.id ? null : p.id); setPenaltyAmt(''); }}
                    title="Kata pointi (adhabu)"
                  >
                    −pt
                  </button>
                  <button style={styles.deletePlayerBtn} onClick={() => setConfirmDelete(p)} title="Futa akaunti">
                    <Icon name="x" size={15} color="#F87171" strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
            {penaltyFor === p.id && (
              <div style={styles.penaltyRow}>
                <span style={styles.penaltyHint}>Kata pointi kama adhabu:</span>
                <input
                  style={styles.penaltyInput}
                  type="number"
                  min="1"
                  value={penaltyAmt}
                  onChange={e => setPenaltyAmt(e.target.value)}
                  placeholder="mf. 50"
                />
                <button style={styles.penaltyGo} disabled={working || !penaltyAmt} onClick={() => deductPoints(p, penaltyAmt)}>Kata</button>
                <button style={styles.cancelBtnSm} onClick={() => setPenaltyFor(null)}>Ghairi</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {note && <div style={styles.toast}>{note}</div>}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: C.bgGradient, padding: '24px 16px 60px' },
  header: { maxWidth: '760px', margin: '0 auto 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  title: { fontFamily: FONT.display, fontSize: '25px', color: C.text, fontWeight: '700' },
  backLink: { color: C.textDim, fontSize: '12.5px', textDecoration: 'none', fontWeight: '600' },
  tourneyLink: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', padding: '10px 16px', borderRadius: '11px', fontSize: '12.5px', fontWeight: '800', textDecoration: 'none' },
  statGrid: { maxWidth: '760px', margin: '0 auto 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' },
  statCard: { background: 'linear-gradient(150deg, #262D2A, #1F2522)', border: `1px solid ${C.line}`, borderRadius: '15px', padding: '14px 12px', textAlign: 'center' },
  statIcon: { width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(52,211,153,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' },
  statNum: { color: C.text, fontFamily: FONT.display, fontSize: '21px', fontWeight: '800' },
  statLbl: { color: C.textFaint, fontSize: '10px', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase', marginTop: '3px' },
  sectionLabel: { maxWidth: '760px', margin: '20px auto 10px', color: C.textDim, fontSize: '11.5px', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' },
  badgeCount: { background: '#F87171', color: '#fff', fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '99px' },
  list: { maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '7px' },
  verifRow: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: '13px', padding: '12px 14px' },
  verifName: { color: C.text, fontSize: '13.5px', fontWeight: '800' },
  verifNick: { color: C.textDim, fontSize: '11.5px', fontWeight: '600' },
  verifMeta: { color: C.textFaint, fontSize: '11px', marginTop: '3px' },
  approveBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rejectBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  gameRow: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`, borderRadius: '13px', padding: '12px 14px', textDecoration: 'none' },
  liveDot: { width: '8px', height: '8px', borderRadius: '50%', background: '#F87171', boxShadow: '0 0 8px #F87171', flexShrink: 0 },
  gameNames: { flex: 1, minWidth: 0, color: C.text, fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '7px' },
  botTag: { background: 'rgba(255,255,255,0.08)', color: C.textDim, fontSize: '9px', fontWeight: '800', padding: '2px 7px', borderRadius: '99px' },
  tourneyTag: { background: 'rgba(52,211,153,0.12)', color: C.accent, fontSize: '9px', fontWeight: '800', padding: '2px 7px', borderRadius: '99px' },
  gameResult: { color: C.accent, fontSize: '12px', fontWeight: '800', flexShrink: 0 },
  gameMeta: { color: C.textFaint, fontSize: '10.5px', flexShrink: 0 },
  emptyRow: { maxWidth: '760px', margin: '0 auto', color: C.textFaint, fontSize: '12.5px', background: 'rgba(255,255,255,0.03)', border: `1px dashed ${C.line}`, borderRadius: '13px', padding: '16px', textAlign: 'center' },
  toast: { position: 'fixed', left: '50%', bottom: '30px', transform: 'translateX(-50%)', background: '#D9F5E7', color: '#06281A', fontSize: '12.5px', fontWeight: '800', padding: '11px 20px', borderRadius: '12px', boxShadow: '0 10px 26px rgba(0,0,0,0.5)', zIndex: 900 },
  resetBox: { maxWidth: '760px', margin: '0 auto 14px', background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '15px', padding: '15px 17px' },
  resetInfo: { marginBottom: '12px' },
  resetTitle: { color: C.text, fontSize: '13.5px', fontWeight: '800' },
  resetText: { color: C.textDim, fontSize: '12px', lineHeight: 1.6, marginTop: '5px' },
  resetBtn: { background: 'rgba(248,113,113,0.14)', border: '1px solid rgba(248,113,113,0.4)', color: '#FCA5A5', fontSize: '12.5px', fontWeight: '800', padding: '11px 18px', borderRadius: '11px', cursor: 'pointer', fontFamily: 'inherit' },
  dangerToggle: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: 'rgba(236,239,234,0.35)', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0', margin: '0 auto 12px', maxWidth: '760px' },
  resetInput: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: '10px', padding: '11px 13px', color: '#ECEFEA', fontSize: '13px', fontFamily: 'inherit', outline: 'none', marginBottom: '10px', letterSpacing: '1px', fontWeight: '800' },
  dangerBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  confirmRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  dangerBtn: { background: 'linear-gradient(135deg, #F87171, #DC2626)', color: '#fff', border: 'none', fontSize: '12.5px', fontWeight: '800', padding: '11px 18px', borderRadius: '11px', cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtn: { background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.line}`, color: C.textDim, fontSize: '12.5px', fontWeight: '700', padding: '11px 16px', borderRadius: '11px', cursor: 'pointer', fontFamily: 'inherit' },
  dangerBtnSm: { background: 'linear-gradient(135deg, #F87171, #DC2626)', color: '#fff', border: 'none', fontSize: '11.5px', fontWeight: '800', padding: '8px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtnSm: { background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.line}`, color: C.textDim, fontSize: '11.5px', fontWeight: '700', padding: '8px 12px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit' },
  playerRow: { display: 'flex', alignItems: 'center', gap: '11px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`, borderRadius: '13px', padding: '10px 13px' },
  playerAvatar: { width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(52,211,153,0.12)', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800', flexShrink: 0 },
  playerName: { color: C.text, fontSize: '13.5px', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  playerMeta: { color: C.textFaint, fontSize: '11px', marginTop: '2px' },
  deletePlayerBtn: { width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  penaltyBtn: { height: '34px', padding: '0 11px', borderRadius: '10px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', color: '#FBBF24', fontSize: '12px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  penaltyRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '2px 0 4px', padding: '10px 13px', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '12px' },
  penaltyHint: { color: C.textDim, fontSize: '11.5px', fontWeight: '700' },
  penaltyInput: { width: '80px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, borderRadius: '9px', padding: '8px 10px', color: C.text, fontSize: '13px', fontFamily: 'inherit', outline: 'none' },
  penaltyGo: { background: 'linear-gradient(135deg, #FBBF24, #F59E0B)', color: '#3A2606', border: 'none', fontSize: '12px', fontWeight: '800', padding: '8px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit' },
  centerMsg: { color: C.text, textAlign: 'center', padding: '90px 20px', fontSize: '14px' },
  link: { color: C.accent, fontWeight: '700' },
};
